<script lang="ts">
    import { onMount, afterUpdate } from 'svelte';
    import { get } from 'svelte/store';

    import { customerSettings } from '../../../stores/api';
    import { blur } from 'svelte/transition';
    import type { CountType, VehicleRequestType, VehicleResponseType } from './types';
    import VehicleOne from './VehicleOne.svelte';
    import { type VehicleGroupResponseType, VehiclesApi } from './VehiclesApi';
    import { api } from '../../../stores/api';
    import { currentMainFilter, filter } from '../../../stores/filter';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import MessageFiltered from '../../shared/MessageFiltered.svelte';
    import FilterVehicles from '../Filter/FilterVehicles.svelte';
    import { vehicleDefaults } from './types';
    import type { WarehouseResponseType } from '../Warehouses/WarehouseApi';
    import { WarehouseApi } from '../Warehouses/WarehouseApi';
    import { _ } from 'svelte-i18n';
    import { Navigation } from '../../utilities/Navigation';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import Separator from '../../shared/Separator.svelte';
    import MessageNotFound from '../../shared/Messages/MessageNotFound.svelte';
    import VehicleOneCompactMode from './VehicleOneCompactMode.svelte';
    import type { QueriesType } from '../telematics/types';
    import { debug } from '../../../stores/store';
    import { vehiclesManager } from './VehiclesApp/vehiclesManager';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import { filterVehiclesOnlyEnabled, filterVehiclesQuery } from '../Filter/FilterVehiclesStore';

    export let addMode: boolean = false;
    export let letAddVehicle: boolean;
    export let compactMode: boolean = false;
    export let activeOnly: boolean = false;
    export let onSelect: (vehicleId: number | number[]) => void;
    export let writableMode: boolean = true;
    export let alreadyUsedVehicles: number[] = [];
    export let allowUseAlreadyUsedVehicle: boolean = true;
    export let onlyNotUsed: boolean = false;
    export let showFilter: boolean = false;
    export let selectMode: string = 'click';
    export let selectedVehicles: number[] = [];
    export let unSelected: boolean = false;
    export let changeCounter: number = 0;
    export let is_group: boolean = false;
    export let onCountChange: (count: number) => void = (count: number) => {};

    let prevAddMode: boolean = false;
    let expandedMore: boolean = false;

    let filteredVehicles: VehicleResponseType[] = [];
    let error: string = '';
    let loading: boolean = true;
    let vehicleFilter: ApiFilterBuilder = new ApiFilterBuilder(null);
    let countTotal: CountType = { count: 0 };
    let warehouses: WarehouseResponseType[] = [];
    let expandedIndex: number = -1;
    let newVehicleContainer: HTMLDivElement;
    let queryFilter: QueriesType = {};

    let vehicleGroupItems: { id: string; label: string }[] = [];
    let vehicleGroups: VehicleGroupResponseType[] = [];

    function filterBy(query: string, onlyEnabled: boolean) {
        vehicleFilter.clearFilterBy();
        vehicleFilter.sortBy('id', 'asc'); // add default sorting
        vehicleFilter.addFilterByStringILike('name', query);
        if (onlyEnabled) {
            vehicleFilter.addFilterByString('enabled', onlyEnabled ? 'true' : 'false');
        }
        // queryFilter = {
        //     // name: query,
        //     // enabled: onlyEnabled,
        //     active: activeOnly
        // }
        // if (activeOnly) {
        //     vehicleFilter.addFilterByString('active', activeOnly ? 'true' : 'false');
        // }
    }

    function setFilteredVehicles(vehiclesResponse: VehicleResponseType[]) {
        filteredVehicles = vehiclesResponse
            .filter(v => {
                return (
                    !onlyNotUsed || alreadyUsedVehicles.find(id => id == v.id) === undefined
                );
            })
            .filter(v => {
                return !activeOnly || v.active;
            });
        if (unSelected) {
            filteredVehicles.unshift({
                ...vehicleDefaultsCopy,
                id: 0,
                name: $_('zone-without-vehicle'),
                email: ' ',
            });
        }
    }

    function reloadElements() {
        error = '';
        let _updateToken = updateToken;
        // let queryFilter = {
        //     // active: activeOnly,
        // }
        Promise.all([
            VehiclesApi.filter(get(api), vehicleFilter, is_group),
            // VehiclesApi.getWithQueries(get(api), queryFilter, vehicleFilter),
            VehiclesApi.getCount(get(api), new ApiFilterBuilder(null), is_group),
        ])
            .then(([vehiclesResponse, countResponse]) => {
                addMode = false;
                if (updateToken == _updateToken) {
                    setFilteredVehicles(vehiclesResponse);
                    if (countTotal.count != countResponse.count) {
                        onCountChange(countResponse.count);
                    }
                    countTotal = countResponse;
                }
                showFilter = countTotal.count > 0;
            })
            .catch(err => {
                error = err;
            });
    }

    let onUpdateTimeout: any;
    let updateToken: string | null;
    function onUpdateOne(instant: boolean) {
        $vehiclesManager.reset();
        vehicleDefaultsCopy = JSON.parse(JSON.stringify(vehicleDefaults));
        let reload = function () {
            filterBy($filterVehiclesQuery, $filterVehiclesOnlyEnabled);
            reloadElements();
        };
        if (instant) {
            updateToken = null;
            reload();
        } else {
            updateToken = (Date.now() * 1000 + Math.random() * 1000)
                .toString(16)
                .replace(/\./g, '');
            if (onUpdateTimeout) {
                clearTimeout(onUpdateTimeout);
            }
            onUpdateTimeout = setTimeout(reload, 500);
        }
    }

    let vehicleDefaultsCopy: VehicleRequestType;

    onMount(() => {
        loading = true;
        vehicleDefaultsCopy = JSON.parse(JSON.stringify(vehicleDefaults));
        $currentMainFilter = FilterVehicles;
        filterBy($filterVehiclesQuery, $filterVehiclesOnlyEnabled);
        $vehiclesManager.reset();
        Promise.all([
            VehiclesApi.filter(get(api), vehicleFilter, is_group),
            WarehouseApi.getAll(get(api)),
            VehiclesApi.getCount(get(api), new ApiFilterBuilder(null), is_group),
            VehiclesApi.getVehicleGroupItems(get(api))
        ])
            .then(([vehiclesResponse, warehousesResponse, countResponse, vehicleGroupsResponse]) => {
                // use onlyNotUsed to filter only vehicles not exists in alreadyUsedVehicles
                setFilteredVehicles(vehiclesResponse);
                warehouses = warehousesResponse;
                if (countTotal.count != countResponse.count) {
                    onCountChange(countResponse.count);
                }
                countTotal = countResponse;
                letAddVehicle = warehouses.length > 0;

                vehicleGroups = vehicleGroupsResponse;
                console.log('vehicleGroups', vehicleGroups);
            })
            .catch(err => {
                error = err;
            })
            .finally(() => {
                loading = false;
            });

        return () => {
            $currentMainFilter = null;
        };
    });

    afterUpdate(() => {
        if (addMode && addMode != prevAddMode && newVehicleContainer) {
            newVehicleContainer.scrollIntoView();
        }
        prevAddMode = addMode;
    });

    $: {
        changeCounter = changeCounter;
        activeOnly = activeOnly;
        onlyNotUsed = onlyNotUsed;
        filterBy($filterVehiclesQuery, $filterVehiclesOnlyEnabled);
        reloadElements();
    }

    $: console.log('VehicleList, expandedIndex', expandedIndex);
</script>

{#if loading}
    <Spinner />
{:else if warehouses.length === 0 && !compactMode}
    <div
        style="display: flex; justify-content: center; align-items: center; flex-direction: column; padding: 30px;"
    >
        <MessageNotFound text={$_('no_warehouses')} />
        <ButtonMain
            text={$_('create_warehouse')}
            showIcon={false}
            on:click={() => {
                Navigation.navigate('/warehouses');
            }}
        />
    </div>
{:else if addMode}
    <main style="margin-bottom: 20px;">
        <div data-type="new" style="width: 100%;" bind:this={newVehicleContainer}>
            <VehicleOne
                bind:expandedMore
                vehicle={{ ...vehicleDefaultsCopy, id: null, is_group }}
                available_vehicle_options={$customerSettings.available_vehicle_options}
                isNew={true}
                index={-1}
                showHeader={true}
                {onUpdateOne}
                {warehouses}
                {error}
                bind:addMode
            />
        </div>
    </main>
{:else if error}
    <MessageDanger {error} />
{:else}
    {#if !compactMode}
        <MessageFiltered filteredCount={filteredVehicles.length} totalCount={countTotal.count} />
    {/if}
    {#if countTotal.count === 0}
        <MessageNotFound text={$_('no_vehicles')} />
    {:else}
        {#if $debug}
            vehicle_id: {JSON.stringify(filteredVehicles.map(v => v.id))}
        {/if}
        {#if !compactMode}
            <Separator />
        {/if}
        <div style="overflow-y: auto; flex: 1; display: flex; flex-direction: column;">
            {#each filteredVehicles as vehicle, index (vehicle.id)}
                <div out:blur>
                    {#if compactMode && !writableMode}
                        <VehicleOneCompactMode
                            {vehicle}
                            {warehouses}
                            onSelect={() => {
                                if (typeof vehicle.id == 'number') {
                                    if (
                                        allowUseAlreadyUsedVehicle ||
                                        alreadyUsedVehicles.includes(vehicle.id) === false
                                    ) {
                                        if (!selectedVehicles.includes(vehicle.id)) {
                                            if (selectMode === 'checkbox') {
                                                selectedVehicles.push(vehicle.id);
                                            } else {
                                                selectedVehicles = [vehicle.id];
                                            }
                                        } else if (selectMode === 'checkbox') {
                                            selectedVehicles.splice(
                                                selectedVehicles.indexOf(vehicle.id),
                                                1
                                            );
                                        }
                                        onSelect(
                                            selectMode === 'checkbox'
                                                ? selectedVehicles
                                                : selectedVehicles[0]
                                        );
                                    } else {
                                        alert($_('vehicle_already_used'));
                                    }
                                }
                            }}
                            alreadyUsed={alreadyUsedVehicles.find(v => v == vehicle.id) !==
                                undefined}
                            {selectMode}
                            checked={typeof vehicle.id == 'number' &&
                                selectedVehicles.includes(vehicle.id)}
                        />
                    {:else}
                        <VehicleOne
                            bind:expandedMore
                            {vehicle}
                            available_vehicle_options={$customerSettings.available_vehicle_options}
                            isNew={false}
                            showHeader={index === 0}
                            {onUpdateOne}
                            {warehouses}
                            {error}
                            {index}
                            bind:addMode
                            bind:expandedIndex
                            {vehicleGroups}
                        />
                    {/if}
                </div>
            {:else}
                <div
                    style="display: flex; justify-content: center; align-items: center; flex-direction: column; padding: 30px;"
                >
                    <MessageNotFound text={$_('no_filtered_vehicles')} />
                </div>
            {/each}
        </div>
    {/if}
{/if}
